/**
 * CalendarSubscribeActions — Platform-aware calendar subscription buttons.
 *
 * Issue #1513: Google Calendar feed subscription — solve Android webcal:// gap
 *
 * Shows subscribe buttons for Google Calendar, Apple Calendar, and Outlook.
 * Platform detection controls which button gets primary emphasis (all are always visible).
 * Includes copy URL fallback and collapsible manual setup instructions.
 */

import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '../components';
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';
import {
  detectCalendarPlatform,
  buildGoogleCalendarUrl,
  buildAppleCalendarUrl,
  buildOutlookSubscribeUrl,
  type CalendarProvider,
} from '../lib/calendarSubscribe';

interface CalendarSubscribeActionsProps {
  feedUrl: string;
  calendarName: string;
}

export default function CalendarSubscribeActions({
  feedUrl,
  calendarName,
}: CalendarSubscribeActionsProps) {
  const [copied, setCopied] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const detectedPlatform = useMemo(() => detectCalendarPlatform(), []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      toast.success('Feed URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  const handleSubscribe = (provider: CalendarProvider) => {
    let url: string;
    switch (provider) {
      case 'google':
        url = buildGoogleCalendarUrl(feedUrl);
        break;
      case 'apple':
        url = buildAppleCalendarUrl(feedUrl);
        break;
      case 'outlook':
        url = buildOutlookSubscribeUrl(feedUrl, calendarName);
        break;
    }
    window.open(url, '_blank', 'noopener');
  };

  const getVariant = (provider: CalendarProvider): 'primary' | 'secondary' => {
    if (!detectedPlatform) return 'secondary';
    return provider === detectedPlatform ? 'primary' : 'secondary';
  };

  return (
    <div className="space-y-4">
      {/* Subscribe Buttons */}
      <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-3">
        <h2 className="font-medium text-text-primary">Subscribe to this feed</h2>

        <div className="flex flex-col gap-2">
          <Button
            variant={getVariant('google')}
            size="md"
            onClick={() => handleSubscribe('google')}
            className="w-full justify-center min-h-11"
          >
            Add to Google Calendar
          </Button>
          <Button
            variant={getVariant('apple')}
            size="md"
            onClick={() => handleSubscribe('apple')}
            className="w-full justify-center min-h-11"
          >
            Subscribe in Apple Calendar
          </Button>
          <Button
            variant={getVariant('outlook')}
            size="md"
            onClick={() => handleSubscribe('outlook')}
            className="w-full justify-center min-h-11"
          >
            Add to Outlook
          </Button>
        </div>
      </div>

      {/* Copy URL */}
      <div className="bg-oro-surface border border-oro-border rounded-xl p-4 space-y-3">
        <div>
          <label className="text-sm font-medium text-text-primary block mb-2">
            Feed URL
          </label>
          <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-3 break-all">
            <code className="text-xs text-text-secondary font-mono">{feedUrl}</code>
          </div>
        </div>

        <Button
          variant="secondary"
          size="md"
          onClick={handleCopy}
          className="w-full flex items-center justify-center gap-2 min-h-11"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy URL
            </>
          )}
        </Button>
      </div>

      {/* Collapsible Manual Instructions */}
      <div className="bg-oro-surface border border-oro-border rounded-xl">
        <button
          type="button"
          onClick={() => setShowManual(!showManual)}
          className="w-full flex items-center justify-between p-4 min-h-11"
        >
          <span className="text-sm font-medium text-text-secondary">
            Manual setup instructions
          </span>
          {showManual ? (
            <ChevronDown className="w-4 h-4 text-text-muted" />
          ) : (
            <ChevronRight className="w-4 h-4 text-text-muted" />
          )}
        </button>

        {showManual && (
          <div className="px-4 pb-4 space-y-4 border-t border-oro-border pt-3">
            <ManualInstructions
              title="Google Calendar"
              steps={[
                'Open Google Calendar on web (not mobile app)',
                'Click "+" next to "Other calendars"',
                'Select "From URL"',
                'Paste the feed URL and click "Add calendar"',
              ]}
            />
            <ManualInstructions
              title="Apple Calendar"
              steps={[
                'Open Calendar app',
                'File \u2192 New Calendar Subscription (macOS) or add subscription in Settings (iOS)',
                'Paste the feed URL and subscribe',
              ]}
            />
            <ManualInstructions
              title="Outlook"
              steps={[
                'Open Outlook Calendar on web',
                'Click "Add calendar" \u2192 "Subscribe from web"',
                'Paste the feed URL and name the calendar',
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ManualInstructions({ title, steps }: { title: string; steps: string[] }) {
  return (
    <div className="space-y-1.5">
      <h3 className="text-sm font-medium text-text-primary">{title}</h3>
      <ol className="list-decimal list-inside space-y-1 text-xs text-text-secondary pl-2">
        {steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
    </div>
  );
}
